import { ApiService } from "@/services/api/ApiService";

type ConversationImageAttachmentInput = {
  id: string;
  analysisText?: string;
  compressedBase64?: string | null;
  file?: File;
};

export class ConversationMediaService {
  // Fallback compression if we don't have compressedBase64 already in attachment state.
  private static async fileToCompressedBase64(file: File): Promise<string | null> {
    return new Promise((resolve) => {
      const reader = new FileReader();

      reader.onload = () => {
        const result = reader.result;
        if (typeof result !== "string") {
          resolve(null);
          return;
        }

        const img = new Image();
        img.onload = () => {
          try {
            const maxWidth = 1200;
            const scale = img.width > maxWidth ? maxWidth / img.width : 1;
            const targetWidth = img.width * scale;
            const targetHeight = img.height * scale;

            const canvas = document.createElement("canvas");
            canvas.width = targetWidth;
            canvas.height = targetHeight;

            const ctx = canvas.getContext("2d");
            if (!ctx) {
              resolve(null);
              return;
            }

            ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

            const dataUrl = canvas.toDataURL("image/jpeg", 0.7);
            const base64 = dataUrl.includes(",") ? dataUrl.split(",")[1] : dataUrl;
            resolve(base64);
          } catch (error) {
            console.error("[ConversationMediaService] Failed to compress image:", error);
            resolve(null);
          }
        };

        img.onerror = () => resolve(null);
        img.src = result;
      };

      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
    });
  }

  public static async saveConversationImageMedia(params: {
    conversationId: string;
    userId?: string | null;
    attachment: ConversationImageAttachmentInput;
  }): Promise<boolean> {
    try {
      const { conversationId, attachment } = params;

      if (!conversationId) return false;
      if (!attachment?.id) return false;

      const compressedBase64 =
        attachment.compressedBase64 !== undefined && attachment.compressedBase64 !== null
          ? attachment.compressedBase64
          : attachment.file
            ? await this.fileToCompressedBase64(attachment.file)
            : null;

      if (!compressedBase64) {
        console.warn("[ConversationMediaService] Missing compressedBase64, cannot persist image:", {
          conversationId,
          attachmentId: attachment.id,
        });
        return false;
      }

      const response = await ApiService.callEdge<{ success?: boolean }>(
        "conversation-attachments",
        {
          action: "upload",
          conversationId,
          attachmentId: attachment.id,
          imageData: compressedBase64,
          analysisText: attachment.analysisText ?? null,
        },
        "public",
      );
      return response?.success === true;
    } catch (error) {
      console.error("[ConversationMediaService] saveConversationImageMedia failed:", error);
      return false;
    }
  }

  public static async saveConversationImageMedias(params: {
    conversationId: string;
    userId?: string | null;
    attachments: ConversationImageAttachmentInput[];
  }): Promise<{ successCount: number; total: number }> {
    try {
      const { conversationId, userId, attachments } = params;
      if (!conversationId) return { successCount: 0, total: 0 };

      let successCount = 0;
      for (const attachment of attachments) {
        // Sequential upload to reduce concurrency issues on mobile
        // (multiple parallel uploads can stall the UI).
        const ok = await this.saveConversationImageMedia({ conversationId, userId, attachment });
        if (ok) successCount += 1;
      }

      return { successCount, total: attachments.length };
    } catch (error) {
      console.error("[ConversationMediaService] saveConversationImageMedias failed:", error);
      return { successCount: 0, total: 0 };
    }
  }

  public static async saveConversationImageMediasWithResults(params: {
    conversationId: string;
    userId?: string | null;
    attachments: ConversationImageAttachmentInput[];
  }): Promise<{ results: boolean[]; successCount: number; total: number }> {
    try {
      const { conversationId, userId, attachments } = params;
      if (!conversationId) return { results: [], successCount: 0, total: 0 };

      const results: boolean[] = [];
      let successCount = 0;
      for (const attachment of attachments) {
        const ok = await this.saveConversationImageMedia({ conversationId, userId, attachment });
        results.push(ok);
        if (ok) successCount += 1;
      }

      return { results, successCount, total: attachments.length };
    } catch (error) {
      console.error("[ConversationMediaService] saveConversationImageMediasWithResults failed:", error);
      return { results: [], successCount: 0, total: 0 };
    }
  }
}

